import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { alpha } from "@mui/material/styles";
import { store } from "../../../../../../store";
import { getSamples } from "../../../../../../redux/actions/sample";
import { getPatientsByCenter } from "../../../../../../redux/actions/patient";
import { updatePatient } from "../../../../../../services/patientService";
import { SampleFormContent } from "../../sample/SampleForm";
import { normalizeAnalysisColor } from "../../analysis/AnalysisColorPalette";

export const sampleReferenceLabel = (sample = {}) => sample.reference || "-";

export const sampleKitReferenceLabel = (sample = {}) =>
  sample.production?.reference ||
  sample.Production?.reference ||
  sample.productionReference ||
  sample.kitReference ||
  "-";

const formatSampleType = (type) => {
  if (!type) return "";
  return String(type).charAt(0).toUpperCase() + String(type).slice(1);
};

export const sampleTypeLabel = (sample = {}) => {
  const explicitType =
    sample.sampleTypeDefinition?.name ||
    sample.SampleTypeDefinition?.name ||
    sample.sampleType?.name ||
    sample.SampleType?.name ||
    sample.sampleTypeName ||
    sample.type ||
    (typeof sample.sampleType === "string" ? sample.sampleType : "") ||
    sample.primaryType;
  if (explicitType) return formatSampleType(explicitType);

  const analysisSampleTypes = (sample.analyses || [])
    .flatMap((analysis) => analysis.sampleTypes || [])
    .map((sampleType) => formatSampleType(sampleType.name))
    .filter(Boolean);

  return [...new Set(analysisSampleTypes)].join(", ") || "-";
};

export const samplePatientLabel = (sample = {}) =>
  sample.patientCode ||
  sample.patientKey ||
  sample.patient?.patientCode ||
  sample.patient?.patient_code ||
  sample.patient?.code ||
  sample.patient?.number ||
  sample.patient?.id ||
  "-";

const samplePatientInclusionNumber = (sample = {}) => {
  const inclusionNumber =
    sample.inclusionNumber ??
    sample.inclusion_number ??
    sample.patient?.inclusionNumber ??
    sample.patient?.inclusion_number;

  return inclusionNumber === null || inclusionNumber === undefined
    ? ""
    : String(inclusionNumber);
};

export const sampleAnalysisLabel = (sample = {}) => {
  if (sample.analyses?.length) {
    return sample.analyses.map((analysis) => analysis.name).join(", ");
  }

  return sample.analysis?.name || `Sample ${sample.id}`;
};

const sampleAnalysisRecords = (sample = {}) => {
  if (sample.analyses?.length) {
    return sample.analyses;
  }

  return sample.analysis ? [sample.analysis] : [];
};

const SampleAnalysisNames = ({ sample }) => {
  const analyses = sampleAnalysisRecords(sample).filter(
    (analysis) => analysis?.name,
  );

  if (!analyses.length) {
    return sampleAnalysisLabel(sample);
  }

  return analyses.map((analysis, index) => (
    <React.Fragment key={analysis.id ?? `${analysis.name}-${index}`}>
      {index > 0 ? ", " : null}
      <Box
        component="span"
        sx={{
          color: normalizeAnalysisColor(analysis.color, "text.primary"),
          textShadow: "0 0 1px rgba(0, 0, 0, 0.6)",
          fontWeight: analysis.color ? 600 : "inherit",
        }}>
        {analysis.name}
      </Box>
    </React.Fragment>
  ));
};

export const sampleVisitLabel = (sample = {}) => {
  const visitNames = (sample.visits || sample.Visits || [])
    .map((visit) => visit.name)
    .filter(Boolean);

  if (visitNames.length) {
    return [...new Set(visitNames)].join(", ");
  }

  return (
    sample.visit?.name ||
    sample.Visit?.name ||
    sample.visitName ||
    sample.visit_name ||
    sample.selectedVisitName ||
    "-"
  );
};

export const sampleContainerLabel = (sample = {}) => {
  const containerLabels = (sample.analyses || [])
    .map((analysis) => analysis.analysisContainer)
    .filter(Boolean)
    .map((container) => {
      const details = [
        container.type,
        container.size,
        container.sizeUnit,
      ].filter(
        (value) => value !== null && value !== undefined && value !== "",
      );

      return [container.name, details.length ? `(${details.join(" ")})` : ""]
        .filter(Boolean)
        .join(" ");
    });

  return [...new Set(containerLabels)].join(", ") || "-";
};

export const sampleQuantityLabel = (sample = {}) =>
  sample.quantity ?? sample.totalSamples ?? sample.quantitySent ?? "-";

const normalizedEnsembleIds = (values = []) => {
  const normalizedValues = Array.isArray(values) ? values : [values];

  return [
    ...new Set(
      normalizedValues
        .map((value) => value?.id ?? value)
        .filter(
          (value) => value !== null && value !== undefined && value !== "",
        )
        .map((value) => {
          const numericValue = Number(value);
          return Number.isFinite(numericValue)
            ? String(numericValue)
            : String(value).trim();
        }),
    ),
  ].sort();
};

const haveNormalizedIdInCommon = (left, right) => {
  const rightIds = new Set(right);
  return left.some((value) => rightIds.has(value));
};

const samplePatientCode = (sample = {}) =>
  String(
    sample.patientCode ||
      sample.patientKey ||
      sample.patient?.patientCode ||
      sample.patient?.patient_code ||
      sample.patient?.code ||
      sample.patient?.number ||
      "",
  )
    .trim()
    .toLowerCase();

const sampleAnalysisIds = (sample = {}) =>
  normalizedEnsembleIds(
    sample.analysisIds?.length
      ? sample.analysisIds
      : sample.analyses?.length
        ? sample.analyses
        : (sample.analysis?.id ?? sample.selectedAnalysis ?? []),
  );

const sampleVisitIds = (sample = {}) =>
  normalizedEnsembleIds(
    sample.visitIds?.length
      ? sample.visitIds
      : sample.visits?.length
        ? sample.visits
        : sample.Visits?.length
          ? sample.Visits
          : (sample.visit?.id ??
            sample.Visit?.id ??
            sample.selectedVisits ??
            []),
  );

export const hasShipmentSampleVisitConflict = (sample, candidate) => {
  const patientCode = samplePatientCode(sample);
  const candidatePatientCode = samplePatientCode(candidate);

  if (!patientCode || patientCode !== candidatePatientCode) {
    return false;
  }

  return (
    haveNormalizedIdInCommon(
      sampleAnalysisIds(sample),
      sampleAnalysisIds(candidate),
    ) &&
    haveNormalizedIdInCommon(sampleVisitIds(sample), sampleVisitIds(candidate))
  );
};

const sampleCenterId = (sample = {}) =>
  sample.patient?.centerId ||
  sample.patient?.center_id ||
  sample.centerId ||
  sample.center_id ||
  "";

const groupSamplesByPatient = (samples) => {
  const grouped = new Map();

  samples.forEach((sample) => {
    const patientKey =
      sample.patientId ||
      sample.patient?.id ||
      sample.patientCode ||
      sample.patientKey ||
      sample.patient?.patientCode ||
      sample.patient?.patient_code ||
      "unknown";
    const patientId = sample.patientId || sample.patient?.id || "";
    const patientLabel = samplePatientLabel(sample);
    const inclusionNumber = samplePatientInclusionNumber(sample);

    if (!grouped.has(patientKey)) {
      grouped.set(patientKey, {
        key: patientKey,
        label: patientLabel,
        patientId,
        inclusionNumber,
        samples: [],
      });
    }

    if (patientId && !grouped.get(patientKey).patientId) {
      grouped.get(patientKey).patientId = patientId;
    }

    if (inclusionNumber && !grouped.get(patientKey).inclusionNumber) {
      grouped.get(patientKey).inclusionNumber = inclusionNumber;
    }

    grouped.get(patientKey).samples.push(sample);
  });

  return Array.from(grouped.values());
};

const SectionHeader = ({ title, description, action }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: { xs: "flex-start", sm: "center" },
      justifyContent: "space-between",
      flexDirection: { xs: "column", sm: "row" },
      gap: 1.5,
      mb: 2.5,
    }}>
    <Box>
      <Typography
        variant="h6"
        sx={{
          color: "primary.main",
          fontWeight: 700,
          lineHeight: 1.25,
        }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      )}
    </Box>
    {action}
  </Box>
);

const savedSampleGridColumns = {
  xs: "1fr",
  md: "minmax(190px, 2fr) minmax(130px, 0.9fr) minmax(200px, 1.8fr) minmax(130px, 0.8fr) minmax(150px, 0.9fr) auto",
};

const savedSampleHeaderLabelSx = {
  color: "primary.dark",
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

const SavedSampleHeader = ({ t }) => (
  <Box
    sx={(theme) => ({
      display: { xs: "none", md: "grid" },
      gridTemplateColumns: savedSampleGridColumns,
      alignItems: "center",
      gap: 1,
      px: 2,
      py: 1,
      borderBottom: "1px solid",
      borderColor: alpha(theme.palette.primary.main, 0.18),
      bgcolor: alpha(theme.palette.primary.main, 0.07),
    })}>
    <Typography variant="caption" sx={savedSampleHeaderLabelSx}>
      {t("analyses", "Analyses")}
    </Typography>
    <Typography variant="caption" sx={savedSampleHeaderLabelSx}>
      {t("primaryBackupAbbreviation", "PRIM / BU")}
    </Typography>
    <Typography variant="caption" sx={savedSampleHeaderLabelSx}>
      {t("visits", "Visits")}
    </Typography>
    <Typography variant="caption" sx={savedSampleHeaderLabelSx}>
      {t("analysisContainer", "Container")}
    </Typography>
    <Typography variant="caption" sx={savedSampleHeaderLabelSx}>
      {t("totalNumberOfSample", "Total number of sample")}
    </Typography>
    <Typography
      variant="caption"
      align="center"
      sx={{ ...savedSampleHeaderLabelSx, minWidth: 80 }}>
      {t("action", "Action")}
    </Typography>
  </Box>
);

const SavedSampleValue = ({ label, value, title = value }) => (
  <Box sx={{ minWidth: 0, px: 0 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: { xs: "block", md: "none" }, mb: 0.25 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      title={String(title)}
      sx={{
        color: "text.primary",
        fontWeight: 500,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}>
      {value}
    </Typography>
  </Box>
);

const PatientHeaderValue = ({ label, value, required = false }) => (
  <Box
    sx={{
      minWidth: 0,
      minHeight: 40,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      px: 0,
      py: 0.25,
    }}>
    <Typography
      variant="caption"
      sx={{ color: "primary.main", fontWeight: 400, lineHeight: 1.1 }}>
      {label}
      {required ? " *" : ""}
    </Typography>
    <Typography
      variant="body1"
      title={value || ""}
      sx={{
        color: "primary.dark",
        fontWeight: 700,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}>
      {value || "—"}
    </Typography>
  </Box>
);

const SavedSampleRow = ({ sample, onEdit, onRemove, highlighted, t }) => (
  <Box
    sx={(theme) => ({
      display: "grid",
      gridTemplateColumns: savedSampleGridColumns,
      alignItems: "center",
      gap: 1,
      px: 2,
      py: 1.5,
      borderBottom: "1px solid",
      borderBottomColor: "divider",
      bgcolor: highlighted
        ? alpha(theme.palette.warning.main, 0.2)
        : "transparent",
      boxShadow: highlighted
        ? `inset 4px 0 0 ${theme.palette.warning.main}`
        : "none",
      transition: theme.transitions.create(["background-color", "box-shadow"]),
    })}>
    <SavedSampleValue
      label={t("analyses", "Analyses")}
      value={<SampleAnalysisNames sample={sample} />}
      title={sampleAnalysisLabel(sample)}
    />
    <SavedSampleValue
      label={t("primaryBackupAbbreviation", "PRIM / BU")}
      value={sampleTypeLabel(sample)}
    />
    <SavedSampleValue
      label={t("visits", "Visits")}
      value={sampleVisitLabel(sample)}
    />
    <SavedSampleValue
      label={t("analysisContainer", "Container")}
      value={sampleContainerLabel(sample)}
    />
    <SavedSampleValue
      label={t("totalNumberOfSample", "Total number of sample")}
      value={sampleQuantityLabel(sample)}
    />
    <Box
      sx={{
        minHeight: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
      }}>
      <IconButton
        size="medium"
        color="primary"
        aria-label={t("editSample", "Edit sample")}
        title={t("editSample", "Edit sample")}
        sx={{ width: 36, height: 36 }}
        onClick={onEdit}>
        <EditIcon fontSize="medium" />
      </IconButton>
      <IconButton
        size="medium"
        color="secondary"
        aria-label={t("remove", "Remove")}
        title={t("remove", "Remove")}
        sx={{ width: 36, height: 36 }}
        onClick={onRemove}>
        <CloseIcon fontSize="medium" />
      </IconButton>
    </Box>
  </Box>
);

export const SampleSelectionSection = ({
  selectedSamples = [],
  availableSamples = [],
  centerId,
  studyId,
  onChange,
  title,
  description,
  sectionSx,
  canEdit = true,
  emptyText,
}) => {
  const { t } = useTranslation();
  const patients = useSelector((state) => state.patient?.patients) || [];
  const sectionSequence = React.useRef(0);
  const previousCenterId = React.useRef(centerId);
  const createSectionKey = React.useCallback((prefix = "patient") => {
    sectionSequence.current += 1;
    return `${prefix}-${sectionSequence.current}`;
  }, []);
  const createSectionFromGroup = React.useCallback(
    (group) => ({
      key: createSectionKey("patient"),
      patientId: group.patientId || "",
      patientCode: group.label === "-" ? "" : group.label,
      inclusionNumber: group.inclusionNumber || "",
      draftSampleKeys: [createSectionKey("sample")],
    }),
    [createSectionKey],
  );
  const [patientSections, setPatientSections] = React.useState(() =>
    groupSamplesByPatient(selectedSamples).map((group, index) => ({
      key: `patient-initial-${group.patientId || group.key || index}`,
      patientId: group.patientId || "",
      patientCode: group.label === "-" ? "" : group.label,
      inclusionNumber: group.inclusionNumber || "",
      draftSampleKeys: [createSectionKey("sample")],
    })),
  );
  const [editingSampleIds, setEditingSampleIds] = React.useState(
    () => new Set(),
  );
  const [editingPatientSectionKeys, setEditingPatientSectionKeys] =
    React.useState(() => new Set());
  const [savingPatientSectionKeys, setSavingPatientSectionKeys] =
    React.useState(() => new Set());
  const [patientSectionErrors, setPatientSectionErrors] = React.useState({});
  const [highlightedSampleId, setHighlightedSampleId] = React.useState(null);
  const selectedSamplesRef = React.useRef(selectedSamples);
  selectedSamplesRef.current = selectedSamples;

  const updateSelectedSamples = React.useCallback(
    (nextSamplesOrUpdater) => {
      if (!onChange) return;

      const nextSamples =
        typeof nextSamplesOrUpdater === "function"
          ? nextSamplesOrUpdater(selectedSamplesRef.current)
          : nextSamplesOrUpdater;
      selectedSamplesRef.current = nextSamples;
      onChange(nextSamples);
    },
    [onChange],
  );

  const normalizedPatientCode = React.useCallback(
    (value) =>
      String(value || "")
        .trim()
        .toLowerCase(),
    [],
  );

  const getPatientCode = React.useCallback(
    (patient = {}) =>
      String(
        patient.patientCode ??
          patient.patient_code ??
          patient.code ??
          patient.number ??
          "",
      ),
    [],
  );

  const getPatientInclusionNumber = React.useCallback(
    (patient = {}) =>
      String(patient.inclusionNumber ?? patient.inclusion_number ?? ""),
    [],
  );

  const sectionsMatch = React.useCallback(
    (section, patient) => {
      if (section.patientId && patient.patientId) {
        return String(section.patientId) === String(patient.patientId);
      }

      const sectionCode = normalizedPatientCode(section.patientCode);
      const patientCode = normalizedPatientCode(patient.patientCode);
      return Boolean(sectionCode && patientCode && sectionCode === patientCode);
    },
    [normalizedPatientCode],
  );

  React.useEffect(() => {
    if (centerId) {
      store.dispatch(getPatientsByCenter(centerId));
    }
  }, [centerId]);

  React.useEffect(() => {
    if (String(previousCenterId.current || "") !== String(centerId || "")) {
      previousCenterId.current = centerId;
      setPatientSections([]);
      setEditingSampleIds(new Set());
      setEditingPatientSectionKeys(new Set());
      setSavingPatientSectionKeys(new Set());
      setPatientSectionErrors({});
      setHighlightedSampleId(null);
    }
  }, [centerId]);

  React.useEffect(() => {
    const selectedGroups = groupSamplesByPatient(selectedSamples);

    setPatientSections((currentSections) => {
      let nextSections = currentSections;

      selectedGroups.forEach((group) => {
        const groupPatient = {
          patientId: group.patientId,
          patientCode: group.label === "-" ? "" : group.label,
        };
        const matchingIndex = nextSections.findIndex((section) =>
          sectionsMatch(section, groupPatient),
        );

        if (matchingIndex === -1) {
          nextSections = [...nextSections, createSectionFromGroup(group)];
          return;
        }

        const matchingSection = nextSections[matchingIndex];
        const nextPatientId =
          matchingSection.patientId || group.patientId || "";
        const nextInclusionNumber =
          matchingSection.inclusionNumber || group.inclusionNumber || "";

        if (
          String(matchingSection.patientId) !== String(nextPatientId) ||
          matchingSection.inclusionNumber !== nextInclusionNumber
        ) {
          nextSections = nextSections.map((section, index) =>
            index === matchingIndex
              ? {
                  ...section,
                  patientId: nextPatientId,
                  inclusionNumber: nextInclusionNumber,
                }
              : section,
          );
        }
      });

      return nextSections;
    });
  }, [createSectionFromGroup, sectionsMatch, selectedSamples]);

  const patientOptions = React.useMemo(() => {
    const options = new Map();
    const addPatientOption = (patient = {}) => {
      const patientId = patient.id || patient.patientId || "";
      const patientCode = getPatientCode(patient);

      if (!patientId && !patientCode) return;

      const optionKey = patientId
        ? `id:${patientId}`
        : `code:${normalizedPatientCode(patientCode)}`;
      options.set(optionKey, {
        ...patient,
        id: patientId,
        patientCode,
        inclusionNumber: getPatientInclusionNumber(patient),
      });
    };

    patients.forEach(addPatientOption);
    [...availableSamples, ...selectedSamples]
      .filter((sample) => {
        const currentSampleCenterId = sampleCenterId(sample);
        return (
          !centerId ||
          !currentSampleCenterId ||
          String(currentSampleCenterId) === String(centerId)
        );
      })
      .forEach((sample) => {
        if (sample.patient) {
          addPatientOption(sample.patient);
        } else if (samplePatientLabel(sample) !== "-") {
          addPatientOption({
            id: sample.patientId,
            patientCode: samplePatientLabel(sample),
            inclusionNumber: samplePatientInclusionNumber(sample),
          });
        }
      });

    return Array.from(options.values()).sort((left, right) =>
      getPatientCode(left).localeCompare(getPatientCode(right)),
    );
  }, [
    availableSamples,
    centerId,
    getPatientCode,
    getPatientInclusionNumber,
    normalizedPatientCode,
    patients,
    selectedSamples,
  ]);

  const getPatientOptionValue = React.useCallback(
    (patient) =>
      patient.id
        ? `id:${patient.id}`
        : `code:${normalizedPatientCode(getPatientCode(patient))}`,
    [getPatientCode, normalizedPatientCode],
  );

  const sectionsWithSamples = React.useMemo(() => {
    const assignedSampleIds = new Set();

    return patientSections.map((section) => ({
      ...section,
      samples: selectedSamples.filter((sample) => {
        const samplePatient = {
          patientId: sample.patientId || sample.patient?.id || "",
          patientCode: samplePatientLabel(sample),
        };
        const sampleKey = String(sample.id);

        if (
          assignedSampleIds.has(sampleKey) ||
          !sectionsMatch(section, samplePatient)
        ) {
          return false;
        }

        assignedSampleIds.add(sampleKey);
        return true;
      }),
    }));
  }, [patientSections, sectionsMatch, selectedSamples]);

  const handleAddPatient = () => {
    setPatientSections((currentSections) => [
      ...currentSections,
      {
        key: createSectionKey("patient"),
        patientId: "",
        patientCode: "",
        inclusionNumber: "",
        draftSampleKeys: [createSectionKey("sample")],
      },
    ]);
  };

  const handleSelectPatient = (patient) => {
    const nextPatient = {
      patientId: patient.id || patient.patientId || "",
      patientCode: getPatientCode(patient),
    };

    setPatientSections((currentSections) => {
      if (
        currentSections.some((section) => sectionsMatch(section, nextPatient))
      ) {
        return currentSections;
      }

      return [
        ...currentSections,
        {
          key: createSectionKey("patient"),
          ...nextPatient,
          inclusionNumber: getPatientInclusionNumber(patient),
          draftSampleKeys: [createSectionKey("sample")],
        },
      ];
    });
  };

  const handlePatientSelectChange = (event) => {
    const selectedPatient = patientOptions.find(
      (patient) => getPatientOptionValue(patient) === event.target.value,
    );

    if (selectedPatient) {
      handleSelectPatient(selectedPatient);
    }
  };

  const handlePatientFieldChange = (sectionKey, field, value) => {
    setPatientSections((currentSections) =>
      currentSections.map((section) =>
        section.key === sectionKey ? { ...section, [field]: value } : section,
      ),
    );
    setPatientSectionErrors((currentErrors) => ({
      ...currentErrors,
      [sectionKey]: "",
    }));
  };

  const handleSampleCreated = async (
    savedSample,
    sectionKey,
    draftSampleKey,
  ) => {
    setHighlightedSampleId(null);
    updateSelectedSamples((currentSamples) =>
      currentSamples.some(
        (sample) => String(sample.id) === String(savedSample.id),
      )
        ? currentSamples
        : [...currentSamples, savedSample],
    );

    setPatientSections((currentSections) =>
      currentSections.map((section) =>
        section.key === sectionKey
          ? {
              ...section,
              patientId:
                savedSample.patientId ||
                savedSample.patient?.id ||
                section.patientId,
              patientCode:
                samplePatientLabel(savedSample) === "-"
                  ? section.patientCode
                  : samplePatientLabel(savedSample),
              inclusionNumber:
                samplePatientInclusionNumber(savedSample) ||
                section.inclusionNumber,
              draftSampleKeys: [createSectionKey("sample")],
            }
          : section,
      ),
    );

    if (centerId) {
      await Promise.all([
        store.dispatch(getSamples()),
        store.dispatch(getPatientsByCenter(centerId)),
      ]);
    }
  };

  const handleSampleSaved = async (savedSample) => {
    setHighlightedSampleId(null);
    updateSelectedSamples((currentSamples) =>
      currentSamples.map((sample) =>
        String(sample.id) === String(savedSample.id) ? savedSample : sample,
      ),
    );
    setEditingSampleIds((currentIds) => {
      const nextIds = new Set(currentIds);
      nextIds.delete(String(savedSample.id));
      return nextIds;
    });

    if (centerId) {
      await store.dispatch(getSamples());
    }
  };

  const handleRemoveSample = (sampleId) => {
    setHighlightedSampleId((currentId) =>
      String(currentId) === String(sampleId) ? null : currentId,
    );
    setEditingSampleIds((currentIds) => {
      const nextIds = new Set(currentIds);
      nextIds.delete(String(sampleId));
      return nextIds;
    });
    updateSelectedSamples((currentSamples) =>
      currentSamples.filter((sample) => String(sample.id) !== String(sampleId)),
    );
  };

  const validateNewSample = React.useCallback(
    (candidate) => {
      const conflictingSample = selectedSamplesRef.current.find((sample) =>
        hasShipmentSampleVisitConflict(sample, candidate),
      );

      if (!conflictingSample) {
        setHighlightedSampleId(null);
        return "";
      }

      setHighlightedSampleId(String(conflictingSample.id));
      return t(
        "duplicateSampleInShipment",
        "A sample with the same patient code, analyses, and visits is already in this shipment.",
      );
    },
    [t],
  );

  const handleEditSample = (sampleId) => {
    setEditingSampleIds((currentIds) => {
      const nextIds = new Set(currentIds);
      nextIds.add(String(sampleId));
      return nextIds;
    });
  };

  const handleCancelSampleEdit = (sampleId) => {
    setEditingSampleIds((currentIds) => {
      const nextIds = new Set(currentIds);
      nextIds.delete(String(sampleId));
      return nextIds;
    });
  };

  const handleEditPatientSection = (sectionKey) => {
    setEditingPatientSectionKeys((currentKeys) => {
      const nextKeys = new Set(currentKeys);
      nextKeys.add(sectionKey);
      return nextKeys;
    });
    setPatientSectionErrors((currentErrors) => ({
      ...currentErrors,
      [sectionKey]: "",
    }));
  };

  const handleSavePatientSection = async (patientSection) => {
    if (!patientSection.patientId || !patientSection.patientCode.trim()) {
      return;
    }

    setSavingPatientSectionKeys((currentKeys) => {
      const nextKeys = new Set(currentKeys);
      nextKeys.add(patientSection.key);
      return nextKeys;
    });
    setPatientSectionErrors((currentErrors) => ({
      ...currentErrors,
      [patientSection.key]: "",
    }));

    try {
      const savedPatient = await updatePatient(patientSection.patientId, {
        patientCode: patientSection.patientCode.trim(),
        inclusionNumber: patientSection.inclusionNumber.trim() || null,
        centerId,
      });
      const savedPatientCode = getPatientCode(savedPatient);
      const savedInclusionNumber = getPatientInclusionNumber(savedPatient);

      setPatientSections((currentSections) =>
        currentSections.map((section) =>
          section.key === patientSection.key
            ? {
                ...section,
                patientCode: savedPatientCode,
                inclusionNumber: savedInclusionNumber,
              }
            : section,
        ),
      );
      updateSelectedSamples((currentSamples) =>
        currentSamples.map((sample) =>
          String(sample.patientId || sample.patient?.id) ===
          String(patientSection.patientId)
            ? {
                ...sample,
                patientCode: savedPatientCode,
                inclusionNumber: savedInclusionNumber,
                patient: {
                  ...(sample.patient || {}),
                  ...savedPatient,
                  patientCode: savedPatientCode,
                  inclusionNumber: savedInclusionNumber,
                },
              }
            : sample,
        ),
      );
      setEditingPatientSectionKeys((currentKeys) => {
        const nextKeys = new Set(currentKeys);
        nextKeys.delete(patientSection.key);
        return nextKeys;
      });

      if (centerId) {
        try {
          await store.dispatch(getPatientsByCenter(centerId));
        } catch (refreshError) {
          console.error("Failed to refresh patients:", refreshError);
        }
      }
    } catch (error) {
      console.error("Failed to update patient:", error);
      setPatientSectionErrors((currentErrors) => ({
        ...currentErrors,
        [patientSection.key]: t(
          "failedToUpdatePatient",
          "Failed to update patient.",
        ),
      }));
    } finally {
      setSavingPatientSectionKeys((currentKeys) => {
        const nextKeys = new Set(currentKeys);
        nextKeys.delete(patientSection.key);
        return nextKeys;
      });
    }
  };

  const handleRemovePatientSection = async (patientSection) => {
    const result = await Swal.fire({
      text: t(
        "confirmRemovePatientSection",
        "Remove this patient and all their samples from this shipment?",
      ),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("remove", "Remove"),
      cancelButtonText: t("cancel", "Cancel"),
      confirmButtonColor: "#d32f2f",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    const sectionSampleIds = new Set(
      patientSection.samples.map((sample) => String(sample.id)),
    );

    setHighlightedSampleId((currentId) =>
      sectionSampleIds.has(String(currentId)) ? null : currentId,
    );

    updateSelectedSamples((currentSamples) =>
      currentSamples.filter(
        (sample) => !sectionSampleIds.has(String(sample.id)),
      ),
    );
    setPatientSections((currentSections) =>
      currentSections.filter((section) => section.key !== patientSection.key),
    );
    setEditingPatientSectionKeys((currentKeys) => {
      const nextKeys = new Set(currentKeys);
      nextKeys.delete(patientSection.key);
      return nextKeys;
    });
    setPatientSectionErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[patientSection.key];
      return nextErrors;
    });
  };

  return (
    <Paper variant="outlined" sx={sectionSx}>
      <SectionHeader
        title={title || t("samples", "Samples")}
        description={
          description ??
          `${selectedSamples.length} ${t("selected", "selected")}`
        }
        action={
          canEdit ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                aria-label={t("newPatient", "New patient")}
                variant="outlined"
                onClick={handleAddPatient}
                disabled={!centerId}
                startIcon={<AddIcon fontSize="small" />}
                sx={{ minWidth: 128, minHeight: 42 }}>
                {t("newPatient", "New patient").toUpperCase()}
              </Button>
              <FormControl size="small">
                <Select
                  value=""
                  displayEmpty
                  disabled={!centerId}
                  onChange={handlePatientSelectChange}
                  renderValue={() =>
                    t("selectPatient", "Select patient").toUpperCase()
                  }
                  inputProps={{
                    "aria-label": t("selectPatient", "Select patient"),
                  }}
                  sx={{
                    minWidth: 178,
                    minHeight: 42,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontWeight: 600,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.dark",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "primary.contrastText",
                    },
                  }}>
                  {patientOptions.length === 0 ? (
                    <MenuItem disabled value="">
                      {t("noPatientsFound", "No patients found.")}
                    </MenuItem>
                  ) : (
                    patientOptions.map((patient) => (
                      <MenuItem
                        key={getPatientOptionValue(patient)}
                        value={getPatientOptionValue(patient)}>
                        {getPatientCode(patient)}
                        {getPatientInclusionNumber(patient)
                          ? ` — ${getPatientInclusionNumber(patient)}`
                          : ""}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>
          ) : null
        }
      />

      {sectionsWithSamples.length === 0 ? (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            py: 4,
            textAlign: "center",
          }}>
          <Typography color="text.secondary">
            {emptyText || t("noPatientsAddedYet", "No patients added yet.")}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {sectionsWithSamples.map((patientSection) => {
            const isEditingPatient =
              !patientSection.patientId ||
              editingPatientSectionKeys.has(patientSection.key);
            const isSavingPatient = savingPatientSectionKeys.has(
              patientSection.key,
            );

            return (
              <Box
                key={patientSection.key}
                sx={{
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                }}>
                <Box
                  sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.07),
                  })}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "240px 220px",
                      },
                      alignItems: "center",
                      gap: 1,
                      flex: "0 1 auto",
                    }}>
                    {canEdit && isEditingPatient ? (
                      <TextField
                        required
                        size="small"
                        label={t("patientCode", "Patient Code")}
                        value={patientSection.patientCode}
                        sx={(theme) => ({
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "background.paper",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: alpha(
                              theme.palette.primary.main,
                              0.38,
                            ),
                          },
                          "& .MuiInputLabel-root": {
                            color: "primary.main",
                            fontWeight: 400,
                          },
                          "& .MuiInputBase-input": {
                            color: "primary.dark",
                            fontWeight: 700,
                          },
                        })}
                        onChange={(event) =>
                          handlePatientFieldChange(
                            patientSection.key,
                            "patientCode",
                            event.target.value,
                          )
                        }
                      />
                    ) : (
                      <PatientHeaderValue
                        required
                        label={t("patientCode", "Patient Code")}
                        value={patientSection.patientCode}
                      />
                    )}
                    {canEdit && isEditingPatient ? (
                      <TextField
                        size="small"
                        label={t("inclusionNumber", "Inclusion Number")}
                        value={patientSection.inclusionNumber}
                        sx={(theme) => ({
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "background.paper",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: alpha(
                              theme.palette.primary.main,
                              0.38,
                            ),
                          },
                          "& .MuiInputLabel-root": {
                            color: "primary.main",
                            fontWeight: 400,
                          },
                          "& .MuiInputBase-input": {
                            color: "primary.dark",
                            fontWeight: 700,
                          },
                        })}
                        onChange={(event) =>
                          handlePatientFieldChange(
                            patientSection.key,
                            "inclusionNumber",
                            event.target.value,
                          )
                        }
                      />
                    ) : (
                      <PatientHeaderValue
                        label={t("inclusionNumber", "Inclusion Number")}
                        value={patientSection.inclusionNumber}
                      />
                    )}
                  </Box>
                  {canEdit && (
                    <Box
                      sx={{
                        minHeight: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 0.5,
                      }}>
                      {patientSection.patientId && (
                        <IconButton
                          size="medium"
                          color="primary"
                          disabled={
                            isSavingPatient ||
                            (isEditingPatient &&
                              !patientSection.patientCode.trim())
                          }
                          aria-label={
                            isEditingPatient
                              ? t("savePatient", "Save patient")
                              : t("editPatient", "Edit patient")
                          }
                          title={
                            isEditingPatient
                              ? t("savePatient", "Save patient")
                              : t("editPatient", "Edit patient")
                          }
                          sx={{ width: 36, height: 36 }}
                          onClick={() =>
                            isEditingPatient
                              ? handleSavePatientSection(patientSection)
                              : handleEditPatientSection(patientSection.key)
                          }>
                          {isEditingPatient ? (
                            <CheckIcon fontSize="medium" />
                          ) : (
                            <EditIcon fontSize="medium" />
                          )}
                        </IconButton>
                      )}
                      <IconButton
                        size="medium"
                        color="secondary"
                        disabled={isSavingPatient}
                        aria-label={t("removePatient", "Remove patient")}
                        title={t("removePatient", "Remove patient")}
                        sx={{ width: 36, height: 36 }}
                        onClick={() =>
                          handleRemovePatientSection(patientSection)
                        }>
                        <CloseIcon fontSize="medium" />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                {patientSectionErrors[patientSection.key] && (
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ px: 2, py: 0.75, bgcolor: "background.paper" }}>
                    {patientSectionErrors[patientSection.key]}
                  </Typography>
                )}

                {canEdit ? (
                  <Box sx={{ bgcolor: "background.paper" }}>
                    {patientSection.samples.length > 0 && (
                      <SavedSampleHeader t={t} />
                    )}
                    {patientSection.samples.map((sample) =>
                      editingSampleIds.has(String(sample.id)) ? (
                        <Box
                          key={sample.id}
                          sx={(theme) => ({
                            boxShadow:
                              String(highlightedSampleId) === String(sample.id)
                                ? `inset 4px 0 0 ${theme.palette.warning.main}`
                                : "none",
                            bgcolor:
                              String(highlightedSampleId) === String(sample.id)
                                ? alpha(theme.palette.warning.main, 0.2)
                                : "transparent",
                            outline:
                              String(highlightedSampleId) === String(sample.id)
                                ? `2px solid ${theme.palette.warning.main}`
                                : "none",
                            outlineOffset: -2,
                            "& > form": {
                              bgcolor:
                                String(highlightedSampleId) ===
                                String(sample.id)
                                  ? alpha(theme.palette.warning.main, 0.2)
                                  : undefined,
                            },
                          })}>
                          <SampleFormContent
                            layout="horizontal"
                            autoSelectSingleVisit
                            singleAnalysisSelect
                            sampleTypeFieldLabel={t(
                              "primaryBackupAbbreviation",
                              "PRIM / BU",
                            )}
                            sampleId={sample.id}
                            initialSample={sample}
                            centerId={centerId}
                            studyId={studyId}
                            formId={`shipment-sample-${sample.id}`}
                            onSaved={handleSampleSaved}
                            onRemove={() => handleCancelSampleEdit(sample.id)}
                          />
                        </Box>
                      ) : (
                        <SavedSampleRow
                          key={sample.id}
                          sample={sample}
                          t={t}
                          highlighted={
                            String(highlightedSampleId) === String(sample.id)
                          }
                          onEdit={() => handleEditSample(sample.id)}
                          onRemove={() => handleRemoveSample(sample.id)}
                        />
                      ),
                    )}
                    {patientSection.draftSampleKeys.map((draftSampleKey) => (
                      <SampleFormContent
                        key={draftSampleKey}
                        layout="horizontal"
                        autoSelectSingleVisit
                        singleAnalysisSelect
                        sampleTypeFieldLabel={t(
                          "primaryBackupAbbreviation",
                          "PRIM / BU",
                        )}
                        backgroundColor="grey.50"
                        centerId={centerId}
                        studyId={studyId}
                        initialPatientId={patientSection.patientId}
                        initialPatientCode={patientSection.patientCode}
                        initialInclusionNumber={patientSection.inclusionNumber}
                        resetKey={draftSampleKey}
                        formId={`shipment-${draftSampleKey}`}
                        validateBeforeSave={validateNewSample}
                        onSaved={(sample) =>
                          handleSampleCreated(
                            sample,
                            patientSection.key,
                            draftSampleKey,
                          )
                        }
                      />
                    ))}
                    {patientSection.samples.length === 0 &&
                      patientSection.draftSampleKeys.length === 0 && (
                        <Box sx={{ px: 2, py: 2, color: "text.secondary" }}>
                          {t(
                            "noSamplesForPatient",
                            "No samples for this patient yet.",
                          )}
                        </Box>
                      )}
                  </Box>
                ) : (
                  <TableContainer sx={{ bgcolor: "background.paper" }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("analyses", "Analyses")}</TableCell>
                          <TableCell>
                            {t("primaryBackupAbbreviation", "PRIM / BU")}
                          </TableCell>
                          <TableCell>{t("visits", "Visits")}</TableCell>
                          <TableCell>
                            {t("analysisContainer", "Container")}
                          </TableCell>
                          <TableCell>{t("quantity", "Quantity")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {patientSection.samples.map((sample) => (
                          <TableRow key={sample.id}>
                            <TableCell>
                              <SampleAnalysisNames sample={sample} />
                            </TableCell>
                            <TableCell>{sampleTypeLabel(sample)}</TableCell>
                            <TableCell>{sampleVisitLabel(sample)}</TableCell>
                            <TableCell>
                              {sampleContainerLabel(sample)}
                            </TableCell>
                            <TableCell>{sampleQuantityLabel(sample)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

export default SampleSelectionSection;
